import { useCallback, useEffect, useRef, useState } from 'react'

/**
 * Watching a page that is still being rendered out of a PDF.
 *
 * A layout added as a PDF arrives as a row with `status: 'converting'` and
 * becomes `ready` (or `failed`) when the converter has run. Nothing pushes
 * that, so the screen asks — but only while a page is actually converting,
 * and not forever: a converter that has not finished a single page in five
 * minutes is not going to be noticed sooner by asking again, and a card that
 * says "wird vorbereitet" for the rest of the afternoon is a lie the screen
 * is telling on the worker's behalf.
 *
 * After the deadline the watch stops and `stalled` turns true, so the card
 * can say so and offer `resume` — one more look, on a person's request.
 */

export const CONVERSION_POLL_MS = 3_000
export const CONVERSION_GIVE_UP_MS = 5 * 60_000

export function useConversionWatch(convertingPages: number[], refresh: () => void) {
  // The page numbers, not the array identity: a refetch hands us a new array
  // every time and would otherwise restart the deadline on every tick.
  const key = [...convertingPages].sort((a, b) => a - b).join(',')
  const [since, setSince] = useState(() => Date.now())
  const [stalled, setStalled] = useState(false)
  const refreshRef = useRef(refresh)
  refreshRef.current = refresh

  useEffect(() => {
    setSince(Date.now())
    setStalled(false)
  }, [key])

  useEffect(() => {
    if (!key || stalled) return
    const timer = setInterval(() => {
      if (Date.now() - since >= CONVERSION_GIVE_UP_MS) {
        setStalled(true)
        return
      }
      refreshRef.current()
    }, CONVERSION_POLL_MS)
    return () => clearInterval(timer)
  }, [key, since, stalled])

  const resume = useCallback(() => {
    setSince(Date.now())
    setStalled(false)
    refreshRef.current()
  }, [])

  return { stalled: stalled && key.length > 0, resume }
}
